// Acceso a datos de cuentas: perfil propio, equipo, cuota y la Edge Function manage-users.
import { FunctionsFetchError, FunctionsHttpError } from '@supabase/supabase-js';
import type { Role } from '../domain/roles.ts';
import { AppFailure, fromFunctionBody, toAppError } from './errors.ts';
import { supabase } from './supabase.ts';

export interface MyProfile {
  id: string;
  name: string;
  email: string;
  role: Role;
  must_change_password: boolean;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: Role;
  active: boolean;
  must_change_password: boolean;
  created_at: string;
}

export interface StorageUsage {
  used_bytes: number;
  ready_bytes: number;
  pending_bytes: number;
  quota_bytes: number;
  ratio: number;
  level: 'ok' | 'warning' | 'critical' | 'full';
  orders_with_media: number;
  avg_bytes_per_order: number | null;
  last_backup_at: string | null;
  launched_at: string | null;
}

export interface OrderStats {
  active: number;
  ruta: number;
  chequeo_repuestos: number;
  repuesto_despachado: number;
  listo: number;
  unassigned: number | null;
}

function fail(error: unknown): never {
  throw new AppFailure(toAppError(error));
}

export async function fetchMyProfile(): Promise<MyProfile> {
  const { data, error } = await supabase.rpc('get_my_profile');
  if (error) fail(error);
  return data as unknown as MyProfile;
}

export async function fetchTeam(): Promise<TeamMember[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, name, email, role, active, must_change_password, created_at')
    .order('name');
  if (error) fail(error);
  return data;
}

export async function fetchStorageUsage(): Promise<StorageUsage> {
  const { data, error } = await supabase.rpc('get_storage_usage');
  if (error) fail(error);
  return data as unknown as StorageUsage;
}

export async function setStorageQuota(bytes: number): Promise<void> {
  const { error } = await supabase.rpc('set_storage_quota', { p_bytes: bytes });
  if (error) fail(error);
}

export async function fetchOrderStats(): Promise<OrderStats> {
  const { data, error } = await supabase.rpc('get_order_stats');
  if (error) fail(error);
  const stats = data as unknown as OrderStats;
  // Una clave ausente (servidor sin actualizar) cuenta como 0, nunca como vacío.
  return {
    ...stats,
    active: stats.active ?? 0,
    ruta: stats.ruta ?? 0,
    chequeo_repuestos: stats.chequeo_repuestos ?? 0,
    repuesto_despachado: stats.repuesto_despachado ?? 0,
    listo: stats.listo ?? 0,
  };
}

// manage-users --------------------------------------------------------------------------------

type ManageUsersBody =
  | { action: 'create_technician' | 'create_operator'; name: string; email: string; password: string }
  | { action: 'set_active'; user_id: string; active: boolean }
  | { action: 'update_profile'; user_id: string; name: string; email: string }
  | { action: 'reset_password'; user_id: string; password: string }
  | { action: 'change_own_password'; password: string };

async function manageUsers(body: ManageUsersBody): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke<Record<string, unknown>>('manage-users', { body });
  if (error) {
    if (error instanceof FunctionsHttpError) {
      const response = error.context as Response;
      let payload: unknown = null;
      try {
        payload = await response.json();
      } catch {
        // cuerpo no JSON: se usa el mensaje genérico
      }
      throw new AppFailure(fromFunctionBody(payload));
    }
    if (error instanceof FunctionsFetchError) throw new AppFailure(toAppError(new TypeError('fetch')));
    fail(error);
  }
  return data ?? {};
}

export function createAccount(
  role: Extract<Role, 'technician' | 'operator'>,
  name: string,
  email: string,
  password: string,
) {
  return manageUsers({ action: role === 'operator' ? 'create_operator' : 'create_technician', name, email, password });
}

export function setAccountActive(userId: string, active: boolean) {
  return manageUsers({ action: 'set_active', user_id: userId, active });
}

export function updateAccount(userId: string, name: string, email: string) {
  return manageUsers({ action: 'update_profile', user_id: userId, name, email });
}

export function resetAccountPassword(userId: string, password: string) {
  return manageUsers({ action: 'reset_password', user_id: userId, password });
}

export function changeOwnPassword(password: string) {
  return manageUsers({ action: 'change_own_password', password });
}
